import { InjectionToken, makeEnvironmentProviders, type EnvironmentProviders } from '@angular/core';
import type { AskRequestBody, RagCollectionRef } from '../models/ask.models';

/** Where the launcher sits on the page. */
export type RagAskPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

/** `auto` follows the host's `prefers-color-scheme`. */
export type RagAskTheme = 'light' | 'dark' | 'auto';

/**
 * How to read the ask response.
 * - `json`: single JSON body (`AskApiResponse`) — the default backend contract.
 * - `stream`: SSE (`text/event-stream`) or NDJSON of `AskStreamChunk`.
 * - `auto`: decided from the response `Content-Type`.
 */
export type RagAskStreamMode = 'auto' | 'json' | 'stream';

/** Use the host Angular HTTP client, or an explicitly configured fetch transport. */
export type RagAskTransport = 'auto' | 'http' | 'fetch';

/** Every user-visible string, so hosts can translate the widget. */
export interface RagAskLabels {
  launcherOpen: string;
  launcherClose: string;
  panelTitle: string;
  panelSubtitle: string;
  welcomeHeading: string;
  welcomeHint: string;
  placeholder: string;
  send: string;
  stop: string;
  reset: string;
  minimize: string;
  expand: string;
  collapse: string;
  disclaimer: string;
  sourcesLabel: string;
  extractUnavailable: string;
  showExtract: string;
  hideExtract: string;
  sourcePreview: string;
  genericError: string;
  networkError: string;
  charsRemaining: string;
  collectionPick: string;
  collectionClear: string;
  allCollections: string;
  freshnessFresh: string;
  freshnessStale: string;
  freshnessUnknown: string;
  updatedOn: string;
  createdOn: string;
}

export interface RagAskConfig {
  /**
   * Base URL of the RAG API, e.g. `https://api.example.org/api_rag`.
   * Combined with {@link askPath}. A value already ending in the ask path is
   * used as-is, so a full endpoint URL also works.
   *
   * This shared widget never imports the host `environment` file. Bind the
   * host value instead, e.g. `[apiUrl]="environment.baseUrl"` or
   * `provideAssistantRag({ apiUrl: environment.baseUrl })`.
   */
  apiUrl: string;

  /** Path of the ask endpoint appended to {@link apiUrl}. Default `/ask/`. */
  askPath?: string;

  /** Collection id the question is scoped to. Default `'wiki_p8'`. `null` searches everything. */
  collection?: string | number | null;

  /** When non-empty, the composer shows a picker for these collections. */
  collections?: RagCollectionRef[];

  /**
   * Send cookies with the request. Default `false`. Set `true` when the host
   * authenticates via session cookies on a same-site or CORS-credentialed API.
   */
  withCredentials?: boolean;

  /**
   * Default `auto`: reuse the host's HttpClient and its authentication
   * interceptors when available, otherwise use fetch. `http` requires a
   * configured HttpClient; `fetch` opts out of Angular interceptors.
   * An explicit fetchFn always takes precedence.
   */
  transport?: RagAskTransport;

  /** Extra request headers. A function is re-evaluated before every request. */
  headers?:
    | Record<string, string>
    | (() => Record<string, string> | Promise<Record<string, string>>);

  /** Extra fields merged into the JSON request body. */
  extraBody?: Record<string, unknown>;

  /** Fetch options (e.g. `mode`, `cache`). Headers also apply to HttpClient. */
  requestInit?: RequestInit;

  /** Replace the transport — useful for tests or a host-managed HTTP client. */
  fetchFn?: (input: string, init: RequestInit) => Promise<Response>;

  /** Hook to reshape the request body before it is sent. */
  transformRequest?: (body: AskRequestBody) => AskRequestBody;

  /** How to read the response. Default `auto`. */
  streamMode?: RagAskStreamMode;

  /** Send previous turns for follow-up questions. Default `true`. */
  history?: boolean;

  /** Cap the number of past turn pairs sent. Default `8`. 0 = no cap. */
  maxHistoryTurns?: number;

  /**
   * Reveal non-streamed answers progressively. `false` prints instantly.
   * A number sets the target reveal duration in ms. Default `900`.
   * Ignored when the backend genuinely streams.
   */
  typewriter?: boolean | number;

  /** Minimum question length before Send enables. Default `3`. */
  minChars?: number;

  /** Maximum question length. Default `8000`. */
  maxChars?: number;

  /** Show the retrieval score on each source. Default `false`. */
  showScores?: boolean;

  /** Show the source list under answers. Default `true`. */
  showSources?: boolean;

  /** Show freshness badges on sources. Default `true`. */
  showFreshness?: boolean;

  /**
   * Intercept clicks on answer links whose href contains this fragment and
   * fetch them as a blob instead, so authenticated downloads work and mixed
   * content is not blocked. `false` disables. Default `'/download/'`.
   */
  downloadLinkMarker?: string | false;

  /**
   * Backend fallback sentence used when nothing relevant was retrieved. When
   * the answer equals it, retrieved-but-unused sources are hidden.
   */
  noAnswerText?: string;

  // ── Presentation ────────────────────────────────────────────────────────────

  position?: RagAskPosition;
  theme?: RagAskTheme;
  /** Brand colour. Any CSS colour; tints are derived with `color-mix`. */
  accentColor?: string;
  /** Distance from the viewport edges, in px. Default `24`. */
  offset?: number;
  zIndex?: number;
  panelWidth?: number;
  panelHeight?: number;
  /** Open the panel on load. Default `false`. */
  startOpen?: boolean;
  /** Render the panel inline in the host layout instead of floating. */
  inline?: boolean;
  /** Keep the conversation across page loads. Default `'none'`. */
  persist?: 'none' | 'session' | 'local';
  /** Storage key used when {@link persist} is enabled. */
  storageKey?: string;
  /** Locale for dates and times. Defaults to the browser locale. */
  locale?: string;
  labels?: Partial<RagAskLabels>;
}

export type ResolvedRagAskConfig = Required<
  Omit<
    RagAskConfig,
    | 'collection'
    | 'collections'
    | 'headers'
    | 'extraBody'
    | 'requestInit'
    | 'fetchFn'
    | 'transformRequest'
    | 'labels'
    | 'locale'
  >
> & {
  collection: string | number | null;
  collections: RagCollectionRef[];
  headers: RagAskConfig['headers'];
  extraBody: Record<string, unknown>;
  requestInit: RequestInit;
  fetchFn: RagAskConfig['fetchFn'];
  transformRequest: RagAskConfig['transformRequest'];
  labels: RagAskLabels;
  locale: string | undefined;
};

export const ASSISTANT_RAG_LABELS_FR: RagAskLabels = {
  launcherOpen: 'Poser une question',
  launcherClose: 'Fermer l’assistant',
  panelTitle: 'Assistant documentaire',
  panelSubtitle: 'Réponses sourcées dans vos documents',
  welcomeHeading: 'Comment puis-je vous aider ?',
  welcomeHint: 'Posez une question sur vos documents — chaque réponse cite ses sources.',
  placeholder: 'Posez votre question…',
  send: 'Envoyer',
  stop: 'Arrêter',
  reset: 'Nouvelle conversation',
  minimize: 'Réduire',
  expand: 'Agrandir',
  collapse: 'Réduire la fenêtre',
  disclaimer: 'Les réponses peuvent contenir des erreurs — vérifiez les informations importantes.',
  sourcesLabel: 'Sources',
  extractUnavailable: 'Extrait indisponible.',
  showExtract: 'Voir l’extrait de cette source',
  hideExtract: 'Masquer l’extrait de cette source',
  sourcePreview: 'Aperçu de la source',
  genericError: 'Une erreur est survenue. Veuillez réessayer.',
  networkError: 'Service indisponible. Vérifiez votre connexion puis réessayez.',
  charsRemaining: 'caractères restants',
  collectionPick: 'Choisir une collection',
  collectionClear: 'Retirer la collection',
  allCollections: 'Toutes les collections',
  freshnessFresh: 'À jour',
  freshnessStale: 'À vérifier',
  freshnessUnknown: 'Date inconnue',
  updatedOn: 'Màj',
  createdOn: 'Créé',
};

export const ASSISTANT_RAG_DEFAULTS: ResolvedRagAskConfig = {
  apiUrl: '',
  askPath: '/ask/',
  collection: 'wiki_p8',
  collections: [],
  withCredentials: false,
  transport: 'auto',
  headers: undefined,
  extraBody: {},
  requestInit: {},
  fetchFn: undefined,
  transformRequest: undefined,
  streamMode: 'auto',
  history: true,
  maxHistoryTurns: 8,
  typewriter: 900,
  minChars: 3,
  maxChars: 8000,
  showScores: false,
  showSources: true,
  showFreshness: true,
  downloadLinkMarker: '/download/',
  noAnswerText: "Je n'ai pas trouvé d'information pertinente, veuillez reformuler votre question.",
  position: 'bottom-right',
  theme: 'light',
  accentColor: '#b71c1c',
  offset: 24,
  zIndex: 2147483000,
  panelWidth: 420,
  panelHeight: 640,
  startOpen: false,
  inline: false,
  persist: 'none',
  storageKey: 'rag-ask.thread',
  locale: undefined,
  labels: ASSISTANT_RAG_LABELS_FR,
};

/**
 * Optional DI token. Prefer {@link provideAssistantRag}; component inputs win over
 * whatever is provided here, so a host can set global defaults and override
 * them per instance.
 */
export const ASSISTANT_RAG_CONFIG = new InjectionToken<RagAskConfig>('ASSISTANT_RAG_CONFIG');

/** Register widget-wide configuration in an Angular application. */
export function provideAssistantRag(config: Partial<RagAskConfig> & Pick<RagAskConfig, 'apiUrl'>): EnvironmentProviders {
  return makeEnvironmentProviders([{ provide: ASSISTANT_RAG_CONFIG, useValue: config as RagAskConfig }]);
}

/** Merge defaults, DI config and per-instance overrides into one frozen config. */
export function resolveRagAskConfig(
  ...layers: (Partial<RagAskConfig> | null | undefined)[]
): ResolvedRagAskConfig {
  const merged: Record<string, unknown> = { ...ASSISTANT_RAG_DEFAULTS };
  let labels: RagAskLabels = ASSISTANT_RAG_LABELS_FR;

  for (const layer of layers) {
    if (!layer) continue;
    for (const [key, value] of Object.entries(layer)) {
      if (value === undefined) continue;
      if (key === 'labels') {
        labels = { ...labels, ...(value as Partial<RagAskLabels>) };
        continue;
      }
      merged[key] = value;
    }
  }

  merged['labels'] = labels;
  return merged as unknown as ResolvedRagAskConfig;
}

/** Build the absolute ask endpoint from `apiUrl` + `askPath`, tolerating slashes. */
export function askEndpoint(config: Pick<ResolvedRagAskConfig, 'apiUrl' | 'askPath'>): string {
  const base = (config.apiUrl ?? '').replace(/\/+$/, '');
  const path = config.askPath ?? '/ask/';
  if (!base) return path;
  if (base.endsWith(path.replace(/\/+$/, ''))) return base + (path.endsWith('/') ? '/' : '');
  return `${base}/${path.replace(/^\/+/, '')}`;
}
